import React, { useRef, useEffect } from "react";
import ColorPalette from "components/ColorPalette";
import { COLOR_THEMES, ColorTheme } from "lib/models";
import { useAppContext } from "lib/state/TextProvider";
import { updateUserProfile } from "lib/api";

const ColorPaletteSelector = () => {
  const { state, setUserProfileNeedsReload } = useAppContext();
  const selectedTheme: ColorTheme = state.userProfile?.colorTheme ?? "default";
  const selectedThemeRef = useRef<HTMLButtonElement | null>(null);

  // Event handler to update the selected theme.
  const handleThemeSelect = async (colorTheme: ColorTheme): Promise<void> => {
    await updateUserProfile({ colorTheme });
    setUserProfileNeedsReload(true);
  };

  // Whenever selectedTheme changes, scroll the button into view.
  useEffect(() => {
    if (selectedThemeRef.current) {
      selectedThemeRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [selectedTheme]);

  return (
    <div className="bg-palette-5 text-palette-2 rounded-lg border-2 border-palette-3 p-4">
      <h2 className="text-lg font-bold mb-2">Color Palette</h2>
      <div className="mb-4 w-fit">
        <span>Selected: </span>
        <span className="capitalize font-semibold">{selectedTheme}</span>
        <div className="mt-2">
          <ColorPalette theme={selectedTheme} hideLabel={true} />
        </div>
      </div>
      <div className="bg-palette-4 text-palette-2 rounded-lg border-2 border-palette-3">
        <div className="grid grid-cols-3 gap-4 max-h-80 overflow-auto p-4">
          {COLOR_THEMES.map((theme: ColorTheme) => (
            <button
              key={theme}
              type="button"
              ref={theme === selectedTheme ? selectedThemeRef : undefined}
              onClick={() => handleThemeSelect(theme)}
              className={`p-2 rounded-lg border-2 border-palette-3 transition-all duration-200 focus:outline-none ${
                theme === selectedTheme
                  ? "bg-palette-3"
                  : "bg-palette-4 hover:bg-palette-2"
              }`}
            >
              <ColorPalette theme={theme} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ColorPaletteSelector;
